class UIVideo extends HTMLElement {
    constructor() {
        super();
        this.video = this.querySelector('video');
        this.progressWrap = this.querySelector('[data-video-progress-wrap]');
        this.progressBar = this.querySelector('[data-video-progress]');
        this.muteButton = this.querySelector('[data-video="mute"]');
        this.muteIcon = this.querySelector('[data-video-mute-icon]');
        this.unmuteIcon = this.querySelector('[data-video-unmute-icon]');
        this.poster = this.querySelector('[data-video-poster]');
        this.playButton = this.querySelector('[data-video-play]');
        this.playIcon = this.querySelector('[data-video-play-icon]');
        this.pauseIcon = this.querySelector('[data-video-pause-icon]');
    }

    connectedCallback() {
        if (!this.video) return;

        this.video.playsInline = true;
        this.video.muted = true;
        this.video.preload = 'metadata';
        this.video.autoplay = false;
        this.video.removeAttribute('autoplay');

        this.bindEvents();
        this.setupViewportObserver();
        this.updateMuteUI();
        this.updatePlaybackUI();
    }

    disconnectedCallback() {
        this.viewportObserver?.disconnect();
    }

    emitVideo() {
        const src = this.dataset.src;
        if (!src || !this.video) return;

        let nextHref;
        try {
            nextHref = new URL(src, window.location.href).href;
        } catch {
            nextHref = src;
        }

        const current = this.video.currentSrc || this.video.src || '';
        let currentHref = current;
        try {
            if (current) currentHref = new URL(current, window.location.href).href;
        } catch {
            currentHref = current;
        }

        if (currentHref === nextHref) return;

        this.video.src = src;
        this.video.load();
    }

    pauseOtherUiVideos() {
        document.querySelectorAll('ui-video').forEach((el) => {
            if (el === this) return;
            if (typeof el.pause === 'function') el.pause();
        });
    }

    async play() {
        if (!this.video) return;

        this.emitVideo();
        this.pauseOtherUiVideos();
        this.showControls();

        try {
            await this.video.play();
        } catch (e) {
            this.video.muted = true;
            this.updateMuteUI();
            await this.video.play().catch(() => { });
        }
    }

    pause() {
        if (!this.video) return;
        this.video.pause();
        this.hideControls();
    }

    toggleMute() {
        if (!this.video) return;
        this.video.muted = !this.video.muted;
        this.updateMuteUI();
        this.dispatchEvent(
            new CustomEvent('uivideo-mute-preference', {
                bubbles: true,
                composed: true,
                detail: { muted: this.video.muted },
            })
        );
    }

    togglePlayback() {
        if (!this.video) return;

        if (this.video.paused) {
            this.play();
        } else {
            this.pause();
        }
    }

    showControls() {
        this.progressWrap?.classList.remove('opacity-0');
        this.muteButton?.classList.remove('opacity-0');
    }

    hideControls() {
        this.progressWrap?.classList.add('opacity-0');
        this.muteButton?.classList.add('opacity-0');
    }

    updateProgress() {
        if (!this.video || !this.progressBar) return;

        const percentage = this.video.duration
            ? (this.video.currentTime / this.video.duration) * 100
            : 0;

        this.progressBar.style.width = `${percentage}%`;
    }

    updateMuteUI() {
        if (!this.video || !this.muteIcon || !this.unmuteIcon) return;

        if (this.video.muted) {
            this.muteIcon.classList.remove('hidden');
            this.unmuteIcon.classList.add('hidden');
        } else {
            this.muteIcon.classList.add('hidden');
            this.unmuteIcon.classList.remove('hidden');
        }
    }

    updatePlaybackUI() {
        if (!this.video || !this.playButton || !this.playIcon) return;

        const isPaused = this.video.paused;

        if (isPaused) {
            this.playIcon.removeAttribute('hidden');
            this.pauseIcon?.setAttribute('hidden', '');
        } else {
            this.playIcon.setAttribute('hidden', '');
            this.pauseIcon?.removeAttribute('hidden');
        }

        this.playButton.setAttribute('aria-label', isPaused ? 'Play video' : 'Pause video');
        this.playButton.setAttribute('aria-pressed', String(!isPaused));
    }

    handleReady() {
        this.video.classList.remove('opacity-0');
    }

    setupViewportObserver() {
        if (!this.video) return;

        this.viewportObserver = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (!entry.isIntersecting && !this.video.muted) {
                        this.video.muted = true;
                        this.updateMuteUI();
                    }
                }
            },
            { threshold: 0.1 }
        );

        this.viewportObserver.observe(this);
    }

    bindEvents() {
        if (!this.video) return;

        this.video.addEventListener('loadedmetadata', () => this.handleReady());
        this.video.addEventListener('canplay', () => this.handleReady());

        this.video.addEventListener('timeupdate', () => this.updateProgress());

        this.video.addEventListener('play', () => {
            this.pauseOtherUiVideos();
            this.showControls();
            this.updatePlaybackUI();
        });

        this.video.addEventListener('pause', () => {
            this.hideControls();
            this.updatePlaybackUI();
        });

        this.video.addEventListener('ended', () => {
            this.video.currentTime = 0;
            this.updatePlaybackUI();
        });

        this.muteButton?.addEventListener('click', () => this.toggleMute());
        this.playButton?.addEventListener('click', () => this.togglePlayback());

        this.playButton?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.togglePlayback();
            }
        });

        this.muteButton?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.toggleMute();
            }
        });

        this.progressWrap?.addEventListener('click', (e) => {
            if (!this.video?.duration) return;

            const rect = this.progressWrap.getBoundingClientRect();
            const ratio = (e.clientX - rect.left) / rect.width;
            this.video.currentTime = ratio * this.video.duration;
        });
    }
}

customElements.define('ui-video', UIVideo);